import clsx from 'clsx'
import type { MouseEventHandler, PropsWithChildren } from 'react'
import React, { createContext, useContext } from 'react'
import Button from '../Button/Button'
import CheckCircleOutline from '../icons/CheckCircleOutline'
import ErrorOutline from '../icons/ErrorOutline'
import Info from '../icons/Info'
import ReportProblem from '../icons/ReportProblem'
import Progress from '../Progress/Progress'
import Typography from '../Typography/Typography'
import { AriaLiveRole } from '../utilities/aria'

export interface AlertProps {
  /**
   * Custom icon to display as a start adornment of the alert. If not provided, a default icon based on the severity will be used
   */
  icon?: JSX.Element | null
  /**
   * [ARIA live region](https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Guides/Live_regions) role for accessibility.
   * Use `alert` for important, time-sensitive information, or `status` for advisory information
   * @default 'alert'
   */
  role?: AriaLiveRole.Alert | AriaLiveRole.Status
  /**
   * The severity level that determines the color scheme and default icon
   * @default 'success'
   */
  severity?: AlertSeverity
  /**
   * The visual style variant of the alert.
   * `filled` uses solid background colors, `minimal` uses lighter background colors
   * @default 'filled'
   */
  variant?: AlertVariant
}

type AlertVariant = 'filled' | 'minimal'

type AlertSeverity = 'info' | 'error' | 'loading' | 'success' | 'warning'

type AlertColors = {
  [key in AlertVariant]: {
    background: {
      [key in AlertSeverity]: string
    }
    icon: {
      [key in AlertSeverity]: string
    }
  }
}

type AlertIcons = {
  [key in AlertSeverity]: JSX.Element
}

interface AlertState {
  icon: JSX.Element | null
  severity: AlertSeverity
  variant: AlertVariant
}

type AlertContextValues = [AlertState]

const AlertContext = createContext<AlertContextValues | null>(null)

const useAlert = () => useContext(AlertContext) as AlertContextValues

const colors: AlertColors = {
  filled: {
    background: {
      info: 'bg-info-main text-white-100',
      error: 'bg-error-main text-white-100',
      loading: 'bg-info-main text-white-100',
      success: 'bg-success-main text-white-100',
      warning: 'bg-warning-main text-white-100',
    },
    icon: {
      info: 'text-white-100',
      error: 'text-white-100',
      loading: 'text-white-100',
      success: 'text-white-100',
      warning: 'text-white-100',
    },
  },
  minimal: {
    background: {
      info: 'bg-info-lighter text-black-90',
      error: 'bg-error-lighter text-black-90',
      loading: 'bg-info-lighter text-black-90',
      success: 'bg-success-lighter text-black-90',
      warning: 'bg-warning-lighter text-black-90',
    },
    icon: {
      info: 'text-info-main',
      error: 'text-error-main',
      loading: 'text-info-main',
      success: 'text-success-main',
      warning: 'text-warning-main',
    },
  },
}

const icons: AlertIcons = {
  info: <Info />,
  loading: <Progress color="info" size="medium" variant="circular" />,
  error: <ErrorOutline />,
  success: <CheckCircleOutline />,
  warning: <ReportProblem />,
}

function Alert({
  children,
  icon = null,
  role = AriaLiveRole.Alert,
  severity = 'success',
  variant = 'filled',
}: PropsWithChildren<AlertProps>): JSX.Element {
  return (
    <div
      className={clsx(
        'pl-4.5 pr-4.5 pt-2.5 pb-2.5 inline-flex items-center rounded-md flex-grow flex-wrap',
        colors[variant].background[severity],
      )}
      role={role}
    >
      <AlertContext.Provider value={[{ icon, severity, variant }]}>
        {children}
      </AlertContext.Provider>
    </div>
  )
}

function Content({
  children,
}: PropsWithChildren<Record<string, unknown>>): JSX.Element {
  const [{ icon, severity, variant }] = useAlert()

  return (
    <div className="flex flex-grow text-inherit">
      <span
        className={clsx(
          'shrink-0 w-6 h-6 mr-2.5',
          colors[variant].icon[severity],
        )}
      >
        {severity === 'loading' && variant === 'filled' ? (
          <Progress color="inverse" size="medium" variant="circular" />
        ) : (
          icon || icons[severity]
        )}
      </span>
      <div className="flex flex-col justify-center flex-grow">{children}</div>
    </div>
  )
}

function Description({
  children,
}: PropsWithChildren<Record<string, unknown>>): JSX.Element {
  return (
    <Typography color="inherit" variant="body2">
      {children}
    </Typography>
  )
}

function Title({
  children,
}: PropsWithChildren<Record<string, unknown>>): JSX.Element {
  return (
    <Typography color="inherit" component="div" variant="button">
      {children}
    </Typography>
  )
}

function Actions({
  children,
}: PropsWithChildren<Record<string, unknown>>): JSX.Element {
  return (
    <div className="items-end flex flex-grow flex-col ml-2.5">
      <div className="space-x-3">{children}</div>
    </div>
  )
}

interface AlertButtonProps {
  /**
   * Custom data attributes to be added to the button element
   */
  dataSet?: Record<string, string | undefined>
  onClick?: MouseEventHandler<HTMLButtonElement>
}

function AlertButton({
  children,
  dataSet,
  onClick,
}: PropsWithChildren<AlertButtonProps>): JSX.Element {
  const [{ variant }] = useAlert()

  // Children must be wrapped in a span (currently) to satisfy the type requirements of the Button component
  return (
    <Button
      color={variant === 'filled' ? 'inverse' : 'default'}
      dataSet={dataSet}
      onClick={onClick}
      size="small"
      variant="outlined"
    >
      <span>{children}</span>
    </Button>
  )
}

Alert.Button = AlertButton
Alert.Content = Content
Alert.Description = Description
Alert.Title = Title
Alert.Actions = Actions

Alert.displayName = 'Alert'

// Proper display names are necessary to generate correct code snippets in stories
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
Alert.Button.displayName = 'Alert.Button'
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
Alert.Content.displayName = 'Alert.Content'
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
Alert.Description.displayName = 'Alert.Description'
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
Alert.Title.displayName = 'Alert.Title'
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
Alert.Actions.displayName = 'Alert.Actions'

export default Alert
